import { useMemo } from "react";
import { useFireflyMotionSpeedContext } from "@/contexts/FireflyMotionSpeedContext";
import {
  computeFireflyMotionMultiplier,
  isFireflyVisualIntensityCapped,
  resolveFireflyVisualIntensity,
} from "@/lib/firefly/fireflyVisualIntensity";
import { useFireflyVisualIntensity } from "@/components/activity/useFireflyVisualIntensity";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

export type FireflyMotionSpeedMeta = {
  loading: boolean;
  canEdit: boolean;
  /** Platform base before per-user intensity scaling. */
  platformMultiplier: number;
  /** True when OS reduced-motion forces calm intensity. */
  intensityCapped: boolean;
};

/** Platform motion speed composed with per-user visual intensity. */
export function useFireflyMotionSpeedMultiplier(): [
  number,
  FireflyMotionSpeedMeta,
] {
  const { multiplier: platformMultiplier, loading, canEdit } =
    useFireflyMotionSpeedContext();
  const { intensity } = useFireflyVisualIntensity();
  const prefersReducedMotion = usePrefersReducedMotion();

  const effectiveIntensity = resolveFireflyVisualIntensity(
    intensity,
    prefersReducedMotion,
  );
  const effectiveMultiplier = useMemo(
    () =>
      computeFireflyMotionMultiplier(platformMultiplier, effectiveIntensity),
    [platformMultiplier, effectiveIntensity],
  );
  const intensityCapped = isFireflyVisualIntensityCapped(
    intensity,
    prefersReducedMotion,
  );

  return [
    effectiveMultiplier,
    {
      loading,
      canEdit,
      platformMultiplier,
      intensityCapped,
    },
  ];
}
